import { motion } from "framer-motion";
import { Reveal, SectionHeading, Overline } from "./Primitives";
import { LOGISTICS_SERVICES, LOGISTICS_FACTORS, IMAGES } from "@/data/content";
import { Truck, ArrowUpRight, ShieldAlert } from "lucide-react";

export const Logistics = () => {
  const scrollToQuote = () =>
    document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="logistics" className="relative bg-[#0a0a0a] border-y border-white/10 py-20 md:py-32 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <Truck className="text-gold" size={26} />
              <Overline>Logistics &amp; Delivery Services</Overline>
            </div>
            <h2 className="font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white">
              Reliable service. Careful handling. <span className="gold-gradient italic">Delivered with confidence.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed">
              When timing, reliability and careful handling matter, you can count on Perfect Touch. Our logistics
              and delivery services provide dependable, professional and efficient transportation solutions for
              residential and business needs—from local deliveries and pickups to scheduled transportation and
              service support, getting your items where they need to go safely and on time.
            </p>

            <div className="mt-8 grid gap-2.5 sm:grid-cols-2">
              {LOGISTICS_SERVICES.map((s) => (
                <div key={s} className="flex items-center gap-2.5">
                  <span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" />
                  <span className="text-sm text-zinc-300">{s}</span>
                </div>
              ))}
            </div>

            <button
              data-testid="logistics-quote-btn"
              onClick={scrollToQuote}
              className="group mt-10 bg-gold text-black font-semibold px-7 py-4 hover:bg-[#c5a059] transition-colors duration-300 flex items-center gap-2"
            >
              Request a Logistics Estimate
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </Reveal>

          <Reveal delay={0.15}>
            <motion.div
              className="relative overflow-hidden border border-white/10"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.5 }}
            >
              <img
                src={IMAGES.logisticsVan}
                alt="Perfect Touch delivery van on a New York City street"
                className="w-full h-[320px] md:h-[440px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/60 to-transparent" />
            </motion.div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <div className="border border-white/10 bg-[#121212] p-7">
              <Overline className="mb-4">Pricing may depend on</Overline>
              <div className="flex flex-wrap gap-2">
                {LOGISTICS_FACTORS.map((f) => (
                  <span key={f} className="text-xs text-zinc-300 border border-white/10 px-3 py-1.5 font-mono">
                    {f}
                  </span>
                ))}
              </div>
            </div>
            <div className="border border-gold/25 bg-gradient-to-br from-[#151208] to-[#0a0a0a] p-7">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert size={18} className="text-gold" />
                <Overline>Important Policy</Overline>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                All logistics requests are subject to availability, service area, vehicle capacity, safety
                requirements and applicable laws. Certain items, destinations or requests may not be
                accepted. We do not transport hazardous, illegal, prohibited or restricted items.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
